import React, { useEffect, useState } from "react";

import "./stylesLeagueDistribution.css";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import CustomDatePicker from "../../../CommonComponents/CustomDatePicker/CustomDatePicker";
import { API_ROUTES } from "../../../../api/ApiRoutes";

import dayjs from "dayjs";
import { Bar } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const colors = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
  "#393b79",
  "#637939",
  "#8c6d31",
  "#843c39",
  "#7b4173",
  "#3182bd",
  "#e6550d",
  "#31a354",
  "#756bb1",
  "#636363",
  "#e7969c",
  "#9edae5",
  "#cedb9c",
  "#f7b6d2",
  "#c7c7c7",
  "#dbdb8d",
  "#9edae5",
  "#17becf",
  "#aec7e8",
  "#fdae6b",
  "#fdd0a2",
  "#a1d99b",
  "#74c476",
  "#31a354",
  "#74a9cf",
  "#6baed6",
  "#2171b5",
  "#08306b",
  "#67000d",
  "#a50f15",
];

const options = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index",
    intersect: false,
  },
  scales: {
    x: {
      beginAtZero: true,
      stacked: true,
    },
    y: {
      stacked: true,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    datalabels: {
      display: false,
      anchor: "end",
      align: "start",
      formatter: (value) => value.toLocaleString(),
      color: "black",
      font: {
        family: "Poppins-regular",
        weight: "bold",
      },
    },
    tooltip: {
      enabled: true,
      mode: "index",
      intersect: false,
      backgroundColor: "#252729",
      titleFont: {
        family: "Poppins-regular",
        size: 16,
        weight: "bold",
      },
      bodyFont: {
        family: "Poppins-regular",
        size: 14,
      },
      padding: 15,
      displayColors: true,
      overflow: true,
      itemSort: function (a, b) {
        return b.raw - a.raw;
      },
      callbacks: {
        label: function (tooltipItem) {
          const label = tooltipItem.dataset.label || "";
          const value = tooltipItem.raw;
          return `${label}: ${Intl.NumberFormat("en-US").format(value)} Bets`;
        },
      },
    },
  },
};

function LeagueDistribution() {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken
  );

  const [isLoading, setIsLoading] = useState(false);

  const thisMonthRange = [dayjs().startOf("month"), dayjs().endOf("month")];
  const [dates, setDates] = useState(thisMonthRange);

  const [allDates, setAllDates] = useState([]);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "",
        data: [],
        backgroundColor: "",
      },
    ],
  });
  const [leagues, setLeagues] = useState([]);
  const getLeagueDistributionData = async (
    queryString = thisMonthRange,
    isQueryString = true
  ) => {
    setIsLoading(true);

    let startDate = "";
    let endDate = "";

    if (isQueryString) {
      let formattedQueryString = queryString.split("&");
      startDate = dayjs(formattedQueryString[0]).format("YYYY-MM-DD");
      endDate = dayjs(formattedQueryString[1]).format("YYYY-MM-DD");
    } else {
      startDate = queryString[0]?.format("YYYY-MM-DD");
      endDate = queryString[1]?.format("YYYY-MM-DD");
    }

    const params = {
      start_date: startDate,
      end_date: endDate,
    };
    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES?.GET_LEAGUE_DISTRIBUTION_DATA,
        params
      );

      const data = response?.data?.data?.data;

      const leagues = data?.map((item) => item?.league);
      const allDates = Array.from(
        new Set(data.flatMap((league) => league.data.map((d) => d.date)))
      ).sort();

      const datasets = data.map((leagueItem, idx) => {
        const rawData = allDates.map((date) => {
          const entry = leagueItem.data.find((d) => d.date === date);
          return entry ? entry.bet_count : 0;
        });

        return {
          label: leagueItem.league,
          data: rawData.filter((val) => val !== 0),
          backgroundColor: colors[idx % colors.length],
        };
      });
      const chartData = {
        labels: allDates,
        datasets: datasets,
      };

      setChartData(chartData);
      setAllDates(allDates);
      setLeagues(leagues);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getLeagueDistributionData(dates, false);
  }, []);

  return (
    <div className="league-distribution-container">
      <div className="league-distribution-header">
        <h2>League distribution by Bets</h2>
        <CustomDatePicker
          getData={getLeagueDistributionData}
          dates={dates}
          setDates={setDates}
          showPresets={true}
        />
      </div>
      <div className="league-distribution-content">
        {isLoading ? (
          <RebetLoader width={100} height={100} />
        ) : (
          <div className="league-distribution-row">
            <div className="league-distribution-chart">
              <Bar data={chartData} options={options} />
            </div>
            <div className="league-distribution-leagues">
              {leagues.map((league, idx) => (
                <div className="league-distribution-league">
                  <div
                    className="league-distribution-league-color"
                    style={{ backgroundColor: colors[idx % colors.length] }}
                  ></div>
                  <div className="league-distribution-league-name">
                    {league}
                    {/* -{" "}
                    {Intl.NumberFormat("en-US").format(
                      chartData.datasets[idx].data[0]
                    )}{" "}
                    Bets */}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LeagueDistribution;
